import classNames from 'classnames';
import { Pagination as CPagination } from 'cloud-react';
import { ReactNode, useState, useEffect, useCallback, useContext, useImperativeHandle } from 'react';
import InfiniteScroll, { Props as ScrollProps } from 'react-infinite-scroll-component';
import ScreenContext from '@/contexts/screen';
import { smallSizeWidth } from '@/constants';
import Empty from '../Empty';
import styles from './index.less';


export type DataSource<T> = {
  data: T[];
  pagination: Pagination;
}

export type ListRequest<T> = (params?: Omit<Pagination, 'total'>) => Promise<DataSource<T>>

export type ActionType = {
  clear: () => void;
  refresh: () => void;
}

export interface ListProps<T> {
  request: ListRequest<T>;
  mode?: 'scroll' | 'paged';
  params?: {};
  renderItem: (item: T, index: number) => ReactNode;
  rowKey?: UKey;
  loadDataLength?: number;
  scrollThreshold?: ScrollProps['scrollThreshold'];
  onClickItem?: (key: UKey) => void;
  actionRef?: React.MutableRefObject<ActionType | undefined> | ((actionRef: ActionType) => void);
}

const defaultPagination: Pagination = {
  current: 1,
  pageSize: 10,
  total: 99
};

const renderDefaultItem: ListProps<ReactNode>['renderItem'] = x => x;

const List = <T,>({
  request,
  mode = 'scroll',
  params = {},
  renderItem = renderDefaultItem,
  rowKey,
  loadDataLength = 10,
  scrollThreshold = '10px',
  onClickItem,
  actionRef
}: ListProps<T>) => {
  const { width: screenWidth } = useContext(ScreenContext);
  const [data, setData] = useState<DataSource<T>['data']>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<DataSource<T>['pagination']>(defaultPagination);
  const hasMore = (pagination.current * pagination.pageSize) < pagination.total;

  const simplePagination = screenWidth < smallSizeWidth;

  const fetch = useCallback(async (p = {}) => {
    setLoading(true);
    try {
      const res = await request({
        current: 1,
        pageSize: mode === 'scroll' ? loadDataLength : pagination.pageSize,
        ...p
      });
      if (res) {
        setData([...res.data]);
        setPagination(res.pagination);
      }
    } finally {
      setLoading(false);
    }
  }, [mode, loadDataLength, pagination, request]);

  const fetchNext = useCallback(async (current?: Pagination['current']) => {
    setLoading(true);
    try {
      const res = await request({
        current,
        pageSize: loadDataLength,
        ...params
      });
      setData([...data, ...res.data]);
      setPagination(res.pagination);
    } finally {
      setLoading(false);
    }
  }, [loadDataLength, request, data, params]);

  const handlePaginationChange = useCallback(async (current: Pagination['current'], pageSize: Pagination['pageSize']) => {
    setLoading(true);
    try {
      const res = await request({
        current,
        pageSize,
        ...params
      });
      setData(res.data);
      setPagination(res.pagination);
    } finally {
      setLoading(false);
    }
  }, [request, params]);

  useImperativeHandle(actionRef, () => ({
    clear: () => {
      setData([]);
      setPagination(defaultPagination);
    },
    refresh: () => fetch(params)
  }), [fetch, params]);

  useEffect(() => {
    setPagination({
      ...defaultPagination,
      pageSize: loadDataLength
    });
  }, [loadDataLength]);

  const handleLoadMoreData = () => {
    if (hasMore) {
      fetchNext(pagination.current + 1);
    }
  }

  const list = data.length ? data.map((item, index) => (
    <div key={rowKey ? (item[rowKey] as UKey) : index}
      onClick={onClickItem && (() => onClickItem(rowKey ? (item[rowKey] as UKey) : index))}
      className={classNames(styles.itemWrapper, { [styles.hoverable]: onClickItem })}
    >
      <div className={styles.item}>
        {renderItem(item, index)}
      </div>
    </div>
  )) : (loading ? null : (
    <div className={styles.empty}><Empty /></div>
  ));

  return (
    <div className={classNames(styles.list, { [styles.loading]: loading, [styles.paged]: mode === 'paged' })}>
      <span className={styles.spin} />
      {mode === 'scroll' ? (
        <InfiniteScroll
          dataLength={data.length}
          next={handleLoadMoreData}
          hasMore={hasMore}
          scrollThreshold={scrollThreshold}
          loader={data.length ? <div className={styles.loader}>加载中</div> : null}
          endMessage={data.length ? <div className={styles.end}>已加载所有数据</div> : null}
        >
          {list}
        </InfiniteScroll>
      ) : (
        <div className={styles.pageContainer}>
          <ul className={styles.pageContent}>{list}</ul>
          <div className={styles.pagination}>
            <CPagination
              showQuickJumper={!simplePagination}
              showPageSizeOptions={false}
              type={simplePagination ? 'simple' : 'default'}
              total={pagination.total}
              current={pagination.current}
              pageSize={pagination.pageSize}
              onChange={handlePaginationChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default List;
